import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '../ui/Modal';
import { DemTypeahead, type DemTypeaheadOption } from './DemTypeahead';
import {
  deliveryDateWeekdayLabel,
  fetchPowerBiCustomerDetail,
  formatDemCustomerAddressDisplay,
  isDeliveryDateAllowed,
  type PowerBiCustomerDetail,
} from '../../lib/demCustomers';
import { loadPoMappingContext, type DemPoItemOption } from '../../lib/demPoMapping';
import {
  fetchPricesForItems,
  formatSysPrice,
  getNavisionPrice,
  type DemPriceRow,
} from '../../lib/demPoPricing';
import { fetchStandardSalesLines } from '../../lib/demStandardSalesLines';
import { enrichLinesWithCustItemBlocks } from '../../lib/demCustItemBlockList';
import { fetchItemDetails, parseMatchingLidCodes, type DemItemDetail } from '../../lib/demSkuMap';
import { saveCallInOrderToPending } from '../../lib/demCallInOrders';
import { elapsedProcessingSeconds } from '../../lib/demProcessingTime';
import type { DemCustomer } from '../../types/dem';

type LineSource = 'ssl' | 'manual';

type LineDraft = {
  id: string;
  source: LineSource;
  itemCode: string;
  description: string;
  packing: string;
  qty: string;
  uom: string;
  price: string;
  blocked: boolean;
  /** NAV Cust_Item_Block_List reason for this customer × item (warning only). */
  custItemBlockReason?: string;
  itemLocked: boolean;
  /** Raw items.lid_code (PowerBI no_3). */
  lidCode: string;
};

type CallInCreateModalProps = {
  open: boolean;
  onClose: () => void;
  actor: string;
  onSaved: (orderId: string) => void;
};

function newManualLine(): LineDraft {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    source: 'manual',
    itemCode: '',
    description: '',
    packing: '',
    qty: '',
    uom: '',
    price: '',
    blocked: false,
    custItemBlockReason: '',
    itemLocked: false,
    lidCode: '',
  };
}

function singaporeTodayIso(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Singapore',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

function priceForLine(
  prices: DemPriceRow[],
  customers: DemCustomer[],
  customerCode: string,
  shipmentDate: string,
  itemCode: string,
  qty: string,
  uom: string,
  basePrice: number,
): string {
  const code = itemCode.trim();
  if (!code || !customerCode.trim()) return '';
  const amount = getNavisionPrice(prices, {
    itemCode: code,
    custCode: customerCode,
    qty: Number(qty) || 1,
    uom: uom || 'PCS',
    deliveryDate: shipmentDate,
    customers,
    itemBasePrice: basePrice,
  });
  return formatSysPrice(amount);
}

export function CallInCreateModal({ open, onClose, actor, onSaved }: CallInCreateModalProps) {
  const [generalOpen, setGeneralOpen] = useState(true);
  const [linesOpen, setLinesOpen] = useState(true);
  const [customers, setCustomers] = useState<DemCustomer[]>([]);
  const [itemOptions, setItemOptions] = useState<DemPoItemOption[]>([]);
  const [loadingContext, setLoadingContext] = useState(false);
  const [loadingSsl, setLoadingSsl] = useState(false);
  const [customerCode, setCustomerCode] = useState('');
  const [powerBiDetail, setPowerBiDetail] = useState<PowerBiCustomerDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [shipmentDate, setShipmentDate] = useState(singaporeTodayIso());
  const [poNumber, setPoNumber] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([newManualLine()]);
  const [priceCache, setPriceCache] = useState<DemPriceRow[]>([]);
  const [basePriceCache, setBasePriceCache] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sessionStartedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!open) {
      sessionStartedAtRef.current = null;
      return;
    }
    sessionStartedAtRef.current = Date.now();
    setGeneralOpen(true);
    setLinesOpen(true);
    setCustomerCode('');
    setPowerBiDetail(null);
    setLoadingDetail(false);
    setShipmentDate(singaporeTodayIso());
    setPoNumber('');
    setLines([newManualLine()]);
    setPriceCache([]);
    setBasePriceCache({});
    setError(null);
    setBusy(false);
    setLoadingSsl(false);

    let cancelled = false;
    setLoadingContext(true);
    void loadPoMappingContext()
      .then((ctx) => {
        if (cancelled) return;
        setCustomers(ctx.customers.filter((c) => c.status !== 'Inactive'));
        setItemOptions(ctx.itemOptions);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to load customers/items.');
      })
      .finally(() => {
        if (!cancelled) setLoadingContext(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  const selectedCustomer = useMemo(
    () => customers.find((c) => c.code === customerCode) ?? null,
    [customers, customerCode],
  );

  const billToCustomer = useMemo(() => {
    const billTo =
      powerBiDetail?.bill_to_customer_no?.trim() ||
      selectedCustomer?.bill_to_customer_no?.trim() ||
      '';
    if (!billTo) return selectedCustomer;
    return customers.find((c) => c.code === billTo) ?? selectedCustomer;
  }, [customers, selectedCustomer, powerBiDetail]);

  const shipmentAddress =
    powerBiDetail?.shipment_address?.trim() ||
    (selectedCustomer ? formatDemCustomerAddressDisplay(selectedCustomer) : '') ||
    '—';
  const billingAddress =
    powerBiDetail?.billing_address?.trim() ||
    (billToCustomer
      ? formatDemCustomerAddressDisplay(billToCustomer)
      : selectedCustomer
        ? formatDemCustomerAddressDisplay(selectedCustomer)
        : '') ||
    '—';
  const billToCode =
    powerBiDetail?.bill_to_customer_no?.trim() ||
    selectedCustomer?.bill_to_customer_no?.trim() ||
    '';

  const deliveryInstruction =
    powerBiDetail?.delivery_instruction?.trim() ||
    selectedCustomer?.delivery_instruction?.trim() ||
    '';
  const navSchedule =
    powerBiDetail?.delivery_schedule?.trim() ||
    selectedCustomer?.delivery_schedule?.trim() ||
    '';
  const shipmentWeekday = /^\d{4}-\d{2}-\d{2}$/.test(shipmentDate)
    ? deliveryDateWeekdayLabel(shipmentDate)
    : '';

  const customerOptions: DemTypeaheadOption[] = useMemo(
    () =>
      customers.map((c) => ({
        value: c.code,
        label: `${c.code} - ${c.name}`,
        blocked: c.is_blocked,
        customerName: c.name,
        address: c.address,
        address2: c.address_2,
        postal: c.postal || undefined,
      })),
    [customers],
  );

  const itemTypeaheadOptions: DemTypeaheadOption[] = useMemo(
    () =>
      itemOptions.map((item) => ({
        value: item.code,
        label: `${item.code} - ${item.description}`,
        blocked: item.is_blocked,
        description: item.description,
        inventory: item.inventory,
        uom: item.uom,
        packing: item.packing,
      })),
    [itemOptions],
  );

  const deliveryDateOffSchedule =
    Boolean(shipmentDate && selectedCustomer) &&
    !isDeliveryDateAllowed(shipmentDate, selectedCustomer?.allowed_days);
  const deliveryDateTitle = deliveryDateOffSchedule
    ? `Warning: Customer [${customerCode}] does not accept deliveries on ${deliveryDateWeekdayLabel(shipmentDate)}s (DEM allowed days).`
    : undefined;

  const repriceLines = useCallback(
    (
      nextLines: LineDraft[],
      prices: DemPriceRow[],
      bases: Record<string, number>,
      custCode: string,
      date: string,
      custList: DemCustomer[],
    ): LineDraft[] =>
      nextLines.map((line) => ({
        ...line,
        price: priceForLine(
          prices,
          custList,
          custCode,
          date,
          line.itemCode,
          line.qty,
          line.uom,
          bases[line.itemCode.trim().toUpperCase()] ?? 0,
        ),
      })),
    [],
  );

  const ensurePricesForItems = useCallback(
    async (itemCodes: string[]) => {
      const unique = [
        ...new Set(itemCodes.map((c) => c.trim().toUpperCase()).filter(Boolean)),
      ];
      if (unique.length === 0) {
        return { prices: priceCache, bases: basePriceCache };
      }

      const missing = unique.filter(
        (code) => !priceCache.some((p) => p.item === code) && basePriceCache[code] == null,
      );
      // Still fetch prices even if base known — price rows may be missing for new items
      const needPrices = unique.filter((code) => !priceCache.some((p) => p.item === code));
      const needDetails = unique.filter((code) => basePriceCache[code] == null);

      if (needPrices.length === 0 && needDetails.length === 0 && missing.length === 0) {
        return { prices: priceCache, bases: basePriceCache };
      }

      const [fetchedPrices, details] = await Promise.all([
        needPrices.length > 0 ? fetchPricesForItems(needPrices) : Promise.resolve([] as DemPriceRow[]),
        needDetails.length > 0
          ? fetchItemDetails(needDetails)
          : Promise.resolve({} as Record<string, DemItemDetail>),
      ]);

      const nextPrices =
        fetchedPrices.length > 0 ? [...priceCache, ...fetchedPrices] : priceCache;
      const nextBases = { ...basePriceCache };
      for (const [code, detail] of Object.entries(details)) {
        nextBases[code] = detail.base_price ?? 0;
      }

      setPriceCache(nextPrices);
      setBasePriceCache(nextBases);
      return { prices: nextPrices, bases: nextBases };
    },
    [priceCache, basePriceCache],
  );

  const loadSslForCustomer = useCallback(
    async (code: string, custList: DemCustomer[], date: string) => {
      setLoadingSsl(true);
      try {
        const ssl = await fetchStandardSalesLines(code);
        if (ssl.length === 0) {
          setLines([newManualLine()]);
          return;
        }

        const codes = ssl.map((row) => row.item_code);
        const [{ prices, bases }, details] = await Promise.all([
          ensurePricesForItems(codes),
          fetchItemDetails(codes),
        ]);

        const sslLines: LineDraft[] = ssl.map((row) => {
          const detail = details[row.item_code];
          const uom = detail?.uom || 'PCS';
          const qty = '';
          return {
            id: `ssl-${row.item_code}-${Math.random().toString(36).slice(2, 6)}`,
            source: 'ssl' as const,
            itemCode: row.item_code,
            description: detail?.description || row.description,
            packing: detail?.packing || '',
            qty,
            uom,
            price: priceForLine(prices, custList, code, date, row.item_code, qty || '1', uom, bases[row.item_code] ?? 0),
            blocked: detail?.is_blocked ?? false,
            itemLocked: true,
            lidCode: detail?.lid_code || '',
          };
        });
        const withBlocks = await enrichLinesWithCustItemBlocks(code, [...sslLines, newManualLine()]);
        setLines(withBlocks);
        setLinesOpen(true);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load standard sales lines.');
        setLines([newManualLine()]);
      } finally {
        setLoadingSsl(false);
      }
    },
    [ensurePricesForItems],
  );

  const commitCustomer = (code: string) => {
    const next = code.trim();
    setCustomerCode(next);
    setPowerBiDetail(null);
    if (!next) {
      setLines([newManualLine()]);
      return;
    }
    void loadSslForCustomer(next, customers, shipmentDate);
    setLoadingDetail(true);
    void fetchPowerBiCustomerDetail(next)
      .then((detail) => {
        setPowerBiDetail(detail);
      })
      .catch((e) => {
        setError(
          e instanceof Error
            ? e.message
            : 'Could not load delivery schedule / bill-to from PowerBI.',
        );
      })
      .finally(() => setLoadingDetail(false));
  };

  useEffect(() => {
    if (!customerCode.trim() || lines.length === 0) return;
    setLines((prev) =>
      repriceLines(prev, priceCache, basePriceCache, customerCode, shipmentDate, customers),
    );
    // Only reprice when shipment date / customer identity changes — not on every line edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentDate, customerCode]);

  const updateLine = (id: string, patch: Partial<LineDraft>) => {
    setLines((prev) =>
      prev.map((line) => {
        if (line.id !== id) return line;
        const next = { ...line, ...patch };
        if ('qty' in patch || 'uom' in patch || 'itemCode' in patch) {
          next.price = priceForLine(
            priceCache,
            customers,
            customerCode,
            shipmentDate,
            next.itemCode,
            next.qty,
            next.uom,
            basePriceCache[next.itemCode.trim().toUpperCase()] ?? 0,
          );
        }
        return next;
      }),
    );
  };

  const commitItemCode = async (lineId: string, code: string) => {
    const next = code.trim().toUpperCase();
    updateLine(lineId, { itemCode: next });
    if (!next) {
      updateLine(lineId, {
        description: '',
        packing: '',
        uom: '',
        blocked: false,
        custItemBlockReason: '',
        price: '',
        lidCode: '',
      });
      return;
    }
    try {
      const [{ prices, bases }, details] = await Promise.all([
        ensurePricesForItems([next]),
        fetchItemDetails([next]),
      ]);
      const detail = details[next];
      const opt = itemOptions.find((i) => i.code.toUpperCase() === next);
      const description = detail?.description || opt?.description || '';
      const packing = detail?.packing || '';
      const uom = detail?.uom || 'PCS';
      const blocked = detail?.is_blocked ?? opt?.is_blocked ?? false;
      const lidCode = detail?.lid_code || '';

      setLines((prev) => {
        const nextLines = prev.map((line) => {
          if (line.id !== lineId) return line;
          const qty = line.qty;
          return {
            ...line,
            itemCode: next,
            description,
            packing,
            uom,
            blocked,
            lidCode,
            price: priceForLine(
              prices,
              customers,
              customerCode,
              shipmentDate,
              next,
              qty,
              uom,
              bases[next] ?? 0,
            ),
          };
        });
        if (customerCode.trim()) {
          void enrichLinesWithCustItemBlocks(customerCode, nextLines).then(setLines);
        }
        return nextLines;
      });
    } catch {
      /* keep typed code */
    }
  };

  const removeLine = (id: string) => {
    setLines((prev) => {
      const next = prev.filter((row) => row.id !== id);
      return next.length === 0 ? [newManualLine()] : next;
    });
  };

  const handleSubmit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (!selectedCustomer) {
        throw new Error('Select a valid customer number.');
      }
      if (selectedCustomer.is_blocked) {
        throw new Error('This customer is blocked.');
      }
      if (!/^\d{4}-\d{2}-\d{2}$/.test(shipmentDate)) {
        throw new Error('Shipment date is required.');
      }

      const qtyLines = lines.filter((line) => {
        const qty = Number(line.qty);
        return line.itemCode.trim() && Number.isFinite(qty) && qty > 0;
      });
      if (qtyLines.length === 0) {
        throw new Error('Enter quantity on at least one item line.');
      }
      setLines(qtyLines);

      const result = await saveCallInOrderToPending({
        customer_code: selectedCustomer.code,
        delivery_date: shipmentDate,
        delivery_instruction: deliveryInstruction,
        ref_no: poNumber.trim(),
        created_by: actor,
        processing_seconds: (() => {
          const sec = elapsedProcessingSeconds(sessionStartedAtRef.current);
          return sec > 0 ? sec : null;
        })(),
        lines: qtyLines.map((line) => ({
          item_code: line.itemCode,
          description: line.description,
          qty: Number(line.qty),
          uom: line.uom || 'PCS',
        })),
      });
      onSaved(result.order_id);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to submit Manual order.');
    } finally {
      setBusy(false);
    }
  };

  const viewText = (value: string, empty = '—') => {
    const text = value.trim();
    return text || empty;
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New Manual order"
      extraWide
      panelClassName="dem-call-in-modal"
      panelStyle={{
        height: 'min(92vh, 900px)',
        maxHeight: 'min(92vh, 900px)',
      }}
    >
      <div className="dem-call-in-form">
        <div className="dem-call-in-form__scroll">
        {loadingContext ? (
          <p className="delivery-form-card__hint">Loading customers and items…</p>
        ) : null}
        {error ? <p className="alert alert-error">{error}</p> : null}

        <section className="dem-call-in-form__collapse">
          <button
            type="button"
            className="dem-call-in-form__collapse-toggle"
            aria-expanded={generalOpen}
            onClick={() => setGeneralOpen((v) => !v)}
          >
            <span>General</span>
            <span aria-hidden>{generalOpen ? '▾' : '▸'}</span>
          </button>
          {generalOpen ? (
            <div className="dem-call-in-form__header">
              <label className="dem-call-in-form__field">
                <span className="dem-call-in-form__label">Customer no.</span>
                <DemTypeahead
                  value={customerCode}
                  onChange={setCustomerCode}
                  onCommit={commitCustomer}
                  commitOnType={false}
                  options={customerOptions}
                  placeholder="Search customer code or name"
                  ariaLabel="Customer number"
                  disabled={busy || loadingContext}
                  tableMode="customer"
                />
              </label>

              <div className="dem-call-in-form__field">
                <span className="dem-call-in-form__label">Customer name</span>
                <p className="dem-call-in-form__view">
                  {selectedCustomer ? viewText(selectedCustomer.name) : '—'}
                </p>
              </div>

              <label className="dem-call-in-form__field">
                <span className="dem-call-in-form__label">PO number</span>
                <input
                  className="field-input"
                  value={poNumber}
                  onChange={(e) => setPoNumber(e.target.value)}
                  placeholder="Optional — customer PO / blanket PO"
                  disabled={busy}
                  autoComplete="off"
                />
              </label>

              <label className="dem-call-in-form__field">
                <span className="dem-call-in-form__label">Shipment date</span>
                <div className="dem-call-in-form__date-row">
                  <input
                    type="date"
                    className={`field-input${deliveryDateOffSchedule ? ' dem-call-in-form__date--warn' : ''}`}
                    value={shipmentDate}
                    title={deliveryDateTitle}
                    onChange={(e) => setShipmentDate(e.target.value)}
                    disabled={busy}
                  />
                  {shipmentWeekday ? (
                    <span className="dem-call-in-form__weekday" aria-live="polite">
                      {shipmentWeekday}
                    </span>
                  ) : null}
                </div>
                {deliveryDateOffSchedule ? (
                  <span className="dem-call-in-form__warn">{deliveryDateTitle}</span>
                ) : null}
              </label>

              <div className="dem-call-in-form__field">
                <span className="dem-call-in-form__label">Delivery schedule</span>
                <p className="dem-call-in-form__view">
                  {selectedCustomer
                    ? loadingDetail
                      ? 'Loading from PowerBI…'
                      : viewText(navSchedule, 'N/A')
                    : '—'}
                </p>
              </div>

              <div className="dem-call-in-form__field dem-call-in-form__field--full">
                <span className="dem-call-in-form__label">Delivery instruction</span>
                <p className="dem-call-in-form__view">
                  {selectedCustomer
                    ? loadingDetail
                      ? 'Loading from PowerBI…'
                      : viewText(deliveryInstruction, 'N/A')
                    : '—'}
                </p>
              </div>

              <div className="dem-call-in-form__field dem-call-in-form__field--full">
                <span className="dem-call-in-form__label">
                  Billing address
                  {billToCode &&
                  selectedCustomer &&
                  billToCode !== selectedCustomer.code
                    ? ` (Bill-to ${billToCode}${powerBiDetail?.bill_to_name ? ` · ${powerBiDetail.bill_to_name}` : ''})`
                    : ''}
                </span>
                <p className="dem-call-in-form__view dem-call-in-form__view--multiline">
                  {selectedCustomer
                    ? loadingDetail
                      ? 'Loading from PowerBI…'
                      : viewText(billingAddress)
                    : '—'}
                </p>
              </div>

              <div className="dem-call-in-form__field dem-call-in-form__field--full">
                <span className="dem-call-in-form__label">Shipment address</span>
                <p className="dem-call-in-form__view dem-call-in-form__view--multiline">
                  {selectedCustomer
                    ? loadingDetail
                      ? 'Loading from PowerBI…'
                      : viewText(shipmentAddress)
                    : '—'}
                </p>
              </div>
            </div>
          ) : null}
        </section>

        <section className="dem-call-in-form__collapse">
          <button
            type="button"
            className="dem-call-in-form__collapse-toggle"
            aria-expanded={linesOpen}
            onClick={() => setLinesOpen((v) => !v)}
          >
            <span>Sales Lines</span>
            <span aria-hidden>{linesOpen ? '▾' : '▸'}</span>
          </button>
          {linesOpen ? (
            <div className="dem-call-in-form__lines">
              {loadingSsl ? (
                <p className="delivery-form-card__hint">Loading standard sales lines…</p>
              ) : null}
              <div className="dem-call-in-form__lines-head dem-call-in-form__lines-head--priced" aria-hidden>
                <span>Item</span>
                <span>Description</span>
                <span>Packing</span>
                <span>Qty</span>
                <span>UOM</span>
                <span>Price</span>
                <span />
              </div>
              {lines.map((line) => {
                const matchingLids = parseMatchingLidCodes(line.lidCode);
                const blockReason = line.custItemBlockReason?.trim() ?? '';
                return (
                  <div
                    key={line.id}
                    className={`dem-call-in-form__line-wrap${line.source === 'ssl' ? ' dem-call-in-form__line-wrap--ssl' : ''}`}
                  >
                    <div
                      className={`dem-call-in-form__line dem-call-in-form__line--priced${line.source === 'ssl' ? ' dem-call-in-form__line--ssl' : ''}`}
                    >
                      {line.itemLocked ? (
                        <div className="dem-call-in-form__item-cell">
                          <p className="dem-call-in-form__view dem-call-in-form__view--code" title={line.itemCode}>
                            {line.itemCode || '—'}
                          </p>
                          {matchingLids.length > 0 ? (
                            <p className="dem-call-in-form__lid-code" title={matchingLids.join(', ')}>
                              ({matchingLids.join(', ')})
                            </p>
                          ) : null}
                        </div>
                      ) : (
                        <div className="dem-call-in-form__item-cell">
                          <DemTypeahead
                            value={line.itemCode}
                            onChange={(value) => updateLine(line.id, { itemCode: value.toUpperCase() })}
                            onCommit={(value) => void commitItemCode(line.id, value)}
                            commitOnType={false}
                            options={itemTypeaheadOptions}
                            placeholder="Item code"
                            ariaLabel="Item code"
                            disabled={busy || loadingContext}
                            tableMode
                            inputClassName={`field-input dem-call-in-form__item-input${line.blocked ? ' dem-call-in-form__blocked' : ''}`}
                          />
                          {matchingLids.length > 0 ? (
                            <p className="dem-call-in-form__lid-code" title={matchingLids.join(', ')}>
                              ({matchingLids.join(', ')})
                            </p>
                          ) : null}
                        </div>
                      )}
                      <p className="dem-call-in-form__view dem-call-in-form__view--desc" title={line.description}>
                        {line.description || '—'}
                      </p>
                      <p className="dem-call-in-form__view dem-call-in-form__view--packing" title={line.packing}>
                        {line.packing || '—'}
                      </p>
                      <input
                        type="number"
                        className="field-input"
                        min={0}
                        step="any"
                        value={line.qty}
                        onChange={(e) => updateLine(line.id, { qty: e.target.value })}
                        disabled={busy}
                        placeholder="0"
                      />
                      <p className="dem-call-in-form__view dem-call-in-form__view--uom">{line.uom || '—'}</p>
                      <p className="dem-call-in-form__view dem-call-in-form__view--price">{line.price || '—'}</p>
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        disabled={busy}
                        title="Remove line"
                        onClick={() => removeLine(line.id)}
                      >
                        ✕
                      </button>
                    </div>
                    {blockReason ? (
                      <p className="dem-call-in-form__cust-block" title={blockReason}>
                        ⚠ {blockReason}
                      </p>
                    ) : null}
                  </div>
                );
              })}
              <button
                type="button"
                className="btn-secondary btn-sm"
                disabled={busy}
                onClick={() => setLines((prev) => [...prev, newManualLine()])}
              >
                + Add line
              </button>
            </div>
          ) : null}
        </section>
        </div>

        <div className="dem-call-in-form__actions">
          <button type="button" className="btn-secondary" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={busy || loadingContext || loadingSsl}
            onClick={() => void handleSubmit()}
          >
            {busy ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
